import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, ArrowLeft, Package, Mail, MapPin, Truck, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useFarmCart } from '@/context/FarmCartContext';
import { formatPrice } from '@/lib/formatMoney';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useProducerShippingOptions, type ShippingOption } from '@/hooks/useProducerShippingOptions';
import { formatMoney } from '@/lib/formatMoney';

/* ── Fallback pickup option when producer has no shipping options configured ── */
const FALLBACK_PICKUP_ID = '__fallback_pickup__';
const FALLBACK_PICKUP: ShippingOption = {
  id: FALLBACK_PICKUP_ID,
  producer_id: '',
  code: 'pickup',
  label: 'Hämta på gården',
  description: null,
  type: 'pickup',
  price_ore: 0,
  currency: 'SEK',
  is_active: true,
  sort_order: 0,
  shopify_shipping_variant_id: null,
  shopify_shipping_product_id: null,
  shopify_sync_status: 'synced',
  shopify_last_error: null,
  last_synced_at: null,
  created_at: '',
  updated_at: '',
};

/** Ensure at least one shipping option (pickup fallback) is always available */
function buildShippingOptions(producerOptions: ShippingOption[]): ShippingOption[] {
  if (producerOptions.length > 0) return producerOptions;
  return [FALLBACK_PICKUP];
}

interface FarmlyCheckoutDeliveryStepProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
}

interface DeliveryAddress {
  street: string;
  postal_code: string;
  city: string;
}

export function FarmlyCheckoutDeliveryStep({ open, onClose, onBack }: FarmlyCheckoutDeliveryStepProps) {
  const { cart, clearCart } = useFarmCart();
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // Delivery address state
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>({
    street: '',
    postal_code: '',
    city: '',
  });
  const [addressErrors, setAddressErrors] = useState<Partial<DeliveryAddress>>({});

  // Fetch shipping options for this producer
  const { data: rawShippingOptions = [], isLoading: isLoadingShipping } = useProducerShippingOptions(cart?.producerId);

  // Always provide at least one option (pickup fallback)
  const shippingOptions = buildShippingOptions(rawShippingOptions);
  const isFallbackPickup = rawShippingOptions.length === 0;

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email && !customerEmail) {
      setCustomerEmail(user.email);
    }
  }, [user?.email, customerEmail]);

  // Set default shipping option when options load — prefer pickup
  useEffect(() => {
    if (shippingOptions.length > 0 && !selectedShippingOptionId) {
      const pickupOption = shippingOptions.find(opt => opt.type === 'pickup');
      const defaultOption = pickupOption || shippingOptions[0];
      setSelectedShippingOptionId(defaultOption.id);
    }
  }, [shippingOptions, selectedShippingOptionId]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setCustomerNote('');
      setEmailError('');
      setAddressErrors({});
    }
  }, [open]);

  // Get selected shipping option
  const selectedOption = shippingOptions.find(opt => opt.id === selectedShippingOptionId);
  const isDelivery = selectedOption?.type === 'fixed'; // 'fixed' type means delivery with fixed price
  const deliveryType = selectedOption?.type === 'pickup' ? 'pickup' : 'delivery';
  const isSelectedFallback = selectedShippingOptionId === FALLBACK_PICKUP_ID;

  // Clear delivery address when switching away from delivery
  useEffect(() => {
    if (!isDelivery) {
      setDeliveryAddress({ street: '', postal_code: '', city: '' });
      setAddressErrors({});
    }
  }, [isDelivery]);

  // Calculate totals
  const itemsSubtotalOre = cart?.items.reduce(
    (sum, item) => sum + Math.round(item.product.price * 100) * item.quantity,
    0
  ) || 0;
  
  const shippingPriceOre = selectedOption?.price_ore || 0;
  const totalOre = itemsSubtotalOre + shippingPriceOre;
  const currency = cart?.items[0]?.product.currencyCode || 'SEK';

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateAddress = (): boolean => {
    if (!isDelivery) return true;
    
    const errors: Partial<DeliveryAddress> = {};
    if (!deliveryAddress.street.trim()) errors.street = 'Adress krävs';
    if (!deliveryAddress.postal_code.trim()) errors.postal_code = 'Postnummer krävs';
    if (!deliveryAddress.city.trim()) errors.city = 'Ort krävs';
    
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCheckout = async () => {
    if (!cart) return;
    
    // Validate shipping option (fallback is always valid)
    if (!selectedOption) {
      toast({
        title: 'Välj leveransalternativ',
        description: 'Du måste välja ett leveransalternativ för att fortsätta.',
        variant: 'destructive',
      });
      return;
    }

    // Validate email
    if (!customerEmail.trim()) {
      setEmailError('E-postadress krävs');
      return;
    }
    if (!validateEmail(customerEmail)) {
      setEmailError('Ogiltig e-postadress');
      return;
    }
    setEmailError('');

    // Validate address if delivery
    if (!validateAddress()) {
      return;
    }

    setIsCheckingOut(true);

    try {
      // Get auth token if available
      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders: Record<string, string> = {};
      if (session?.access_token) {
        authHeaders['Authorization'] = `Bearer ${session.access_token}`;
      }

      // Build items for checkout
      const checkoutItems = cart.items.map(item => ({
        title: item.product.title,
        quantity: item.quantity,
        unit_amount: Math.round(item.product.price * 100), // Convert to öre
        item_type: 'product' as const,
        product_id: item.product.id,
        metadata: {
          variant_id: item.product.variantId,
          image_url: item.product.imageUrl,
        },
      }));

      // Get current URL for success/cancel redirects
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/checkout/success`;
      const cancelUrl = `${baseUrl}/producer/${cart.producerId}`;

      // Build request body
      // Fallback pickup → send null shipping_option_id with explicit label/price
      const requestBody: Record<string, unknown> = {
        producer_id: cart.producerId,
        items: checkoutItems,
        shipping_option_id: isSelectedFallback ? null : selectedShippingOptionId,
        delivery_type: deliveryType,
        shipping_fee_amount: shippingPriceOre,
        ...(isSelectedFallback && {
          shipping_label: FALLBACK_PICKUP.label,
          shipping_price_ore: 0,
        }),
        customer: {
          email: customerEmail.trim(),
          name: customerName.trim() || undefined,
          phone: customerPhone.trim() || undefined,
        },
        success_url: successUrl,
        cancel_url: cancelUrl,
      };

      // Add delivery address if applicable
      if (isDelivery) {
        requestBody.delivery_address = {
          street: deliveryAddress.street.trim(),
          postal_code: deliveryAddress.postal_code.trim(),
          city: deliveryAddress.city.trim(),
        };
      }

      // Call the Farmly checkout edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-farmly-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            ...authHeaders,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        console.error('Checkout API error:', result);
        toast({
          title: 'Kunde inte starta betalning',
          description: result.error || 'Försök igen eller kontakta gården direkt.',
          variant: 'destructive',
        });
        return;
      }

      // Check if Stripe is disabled (direct order creation)
      if (result.stripe_disabled) {
        // Order created directly — show success page
        clearCart();
        onClose();
        const token = result.public_token;
        navigate(`/checkout/success${token ? `?token=${token}` : ''}`);
        return;
      }

      // Stripe enabled: use checkout_url from response
      const checkoutSessionUrl = result.checkout_url;
      if (!checkoutSessionUrl) {
        throw new Error('No checkout URL returned');
      }

      // Success - clear cart and redirect to payment provider
      clearCart();
      onClose();
      window.location.href = checkoutSessionUrl;

    } catch (err) {
      console.error('Checkout error:', err);
      toast({
        title: 'Kunde inte starta betalning',
        description: 'Ett oväntat fel uppstod. Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsCheckingOut(false);
    }
  };

  const canCheckout = customerEmail.trim() && !isCheckingOut && !!selectedOption && 
    (!isDelivery || (deliveryAddress.street && deliveryAddress.postal_code && deliveryAddress.city));

  // Helper to format shipping price
  const formatShippingPrice = (priceOre: number) => {
    if (priceOre === 0) return 'Gratis';
    return formatMoney(priceOre);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="flex flex-col w-full sm:max-w-md">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onBack}
              disabled={isCheckingOut}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <SheetTitle>Till kassan</SheetTitle>
              {cart && (
                <SheetDescription>
                  Fyll i dina uppgifter för beställningen
                </SheetDescription>
              )}
            </div>
          </div>
        </SheetHeader>

        {!cart || cart.items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Din varukorg är tom</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto py-4 space-y-6">
            {/* Customer info */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Dina uppgifter</Label>
              
              <div className="space-y-2">
                <Label htmlFor="customer-email" className="text-sm">
                  E-postadress *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="customer-email"
                    type="email"
                    placeholder="din@email.se"
                    value={customerEmail}
                    onChange={(e) => {
                      setCustomerEmail(e.target.value);
                      setEmailError('');
                    }}
                    disabled={isCheckingOut}
                    className={`pl-10 ${emailError ? 'border-destructive' : ''}`}
                  />
                </div>
                {emailError && (
                  <p className="text-sm text-destructive">{emailError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer-name" className="text-sm">
                  Namn (valfritt)
                </Label>
                <Input
                  id="customer-name"
                  type="text"
                  placeholder="Ditt namn"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  disabled={isCheckingOut}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer-phone" className="text-sm">
                  Telefon (valfritt)
                </Label>
                <Input
                  id="customer-phone"
                  type="tel"
                  placeholder="070-123 45 67"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  disabled={isCheckingOut}
                />
              </div>
            </div>

            <Separator />

            {/* Shipping options */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Leveransalternativ</Label>
              
              {isLoadingShipping ? (
                <div className="space-y-3">
                  <Skeleton className="h-20 w-full rounded-lg" />
                  <Skeleton className="h-20 w-full rounded-lg" />
                </div>
              ) : (
                <RadioGroup
                  value={selectedShippingOptionId}
                  onValueChange={setSelectedShippingOptionId}
                  className="space-y-3"
                >
                  {shippingOptions.map((option) => {
                    const isPickup = option.type === 'pickup';
                    return (
                      <label 
                        key={option.id}
                        htmlFor={`shipping-${option.id}`}
                        className={`flex items-start gap-3 p-4 border rounded-lg transition-colors ${
                          isCheckingOut 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'cursor-pointer hover:border-primary/50'
                        } ${
                          selectedShippingOptionId === option.id 
                            ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                            : 'border-border'
                        }`}
                        onClick={(e) => {
                          if (isCheckingOut) {
                            e.preventDefault();
                            return;
                          }
                        }}
                      >
                        <RadioGroupItem 
                          value={option.id} 
                          id={`shipping-${option.id}`}
                          className="mt-0.5" 
                          disabled={isCheckingOut}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {isPickup ? (
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <Truck className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="font-medium">{option.label}</span>
                            </div>
                            <Badge 
                              variant="outline" 
                              className={option.price_ore === 0 
                                ? "text-green-600 border-green-200 bg-green-50" 
                                : ""}
                            >
                              {formatShippingPrice(option.price_ore)}
                            </Badge>
                          </div>
                          {option.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {option.description}
                            </p>
                          )}
                          {isPickup && (
                            <p className="text-xs text-muted-foreground mt-2 italic">
                              Exakt upphämtningstid bestäms direkt med producenten efter köp.
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              )}
            </div>

            {/* Delivery address (only shown for delivery options) */}
            {isDelivery && selectedOption && (
              <>
                <Separator />
                <div className="space-y-4">
                  <Label className="text-base font-medium">Leveransadress</Label>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address-street" className="text-sm">
                      Gatuadress *
                    </Label>
                    <Input
                      id="address-street"
                      type="text"
                      placeholder="Exempelgatan 1"
                      value={deliveryAddress.street}
                      onChange={(e) => {
                        setDeliveryAddress(prev => ({ ...prev, street: e.target.value }));
                        setAddressErrors(prev => ({ ...prev, street: undefined }));
                      }}
                      disabled={isCheckingOut}
                      className={addressErrors.street ? 'border-destructive' : ''}
                    />
                    {addressErrors.street && (
                      <p className="text-sm text-destructive">{addressErrors.street}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="address-postal" className="text-sm">
                        Postnummer *
                      </Label>
                      <Input
                        id="address-postal"
                        type="text"
                        placeholder="123 45"
                        value={deliveryAddress.postal_code}
                        onChange={(e) => {
                          setDeliveryAddress(prev => ({ ...prev, postal_code: e.target.value }));
                          setAddressErrors(prev => ({ ...prev, postal_code: undefined }));
                        }}
                        disabled={isCheckingOut}
                        className={addressErrors.postal_code ? 'border-destructive' : ''}
                      />
                      {addressErrors.postal_code && (
                        <p className="text-sm text-destructive">{addressErrors.postal_code}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address-city" className="text-sm">
                        Ort *
                      </Label>
                      <Input
                        id="address-city"
                        type="text"
                        placeholder="Stockholm"
                        value={deliveryAddress.city}
                        onChange={(e) => {
                          setDeliveryAddress(prev => ({ ...prev, city: e.target.value }));
                          setAddressErrors(prev => ({ ...prev, city: undefined }));
                        }}
                        disabled={isCheckingOut}
                        className={addressErrors.city ? 'border-destructive' : ''}
                      />
                      {addressErrors.city && (
                        <p className="text-sm text-destructive">{addressErrors.city}</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Customer note */}
            <div className="space-y-2">
              <Label htmlFor="customer-note" className="text-sm font-medium">
                Meddelande till producenten (valfritt)
              </Label>
              <Textarea
                id="customer-note"
                placeholder="T.ex. önskemål om upphämtningstid..."
                value={customerNote}
                onChange={(e) => setCustomerNote(e.target.value)}
                disabled={isCheckingOut}
                className="resize-none"
                rows={3}
              />
            </div>

            <Separator />

            {/* Order summary */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Sammanfattning</h4>
              
              {/* Selected delivery method */}
              {selectedOption && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  {selectedOption.type === 'pickup' ? (
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Truck className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm">{selectedOption.label}</span>
                  <span className={`ml-auto text-sm font-medium ${selectedOption.price_ore === 0 ? 'text-green-600' : ''}`}>
                    {formatShippingPrice(selectedOption.price_ore)}
                  </span>
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Varor ({cart.items.length} st)</span>
                  <span>{formatPrice(itemsSubtotalOre / 100, currency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Leverans</span>
                  <span className={shippingPriceOre === 0 ? 'text-green-600' : ''}>
                    {formatShippingPrice(shippingPriceOre)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold text-lg">
                  <span>Totalt</span>
                  <span>{formatPrice(totalOre / 100, currency)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <SheetFooter className="flex-col gap-4 pt-4 sm:flex-col">
          <Button
            className="w-full gap-2"
            size="lg"
            onClick={handleCheckout}
            disabled={!canCheckout}
          >
            {isCheckingOut ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Skickar beställning...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Skicka beställning
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Din beställning skickas till producenten
          </p>
        </SheetFooter>
          <div className="bottom-sheet-spacer" />
      </SheetContent>
    </Sheet>
  );
}
